/**
 * The document context — what is in the open Revit model, as the plugin wrote
 * it to `%APPDATA%\BimGo\{year}\active_context.json` and the local agent serves
 * it (`GET /document-context`, item BH5). This is the client and the types;
 * the caches live in `hooks/useDocumentContext.ts`.
 *
 * Ported from `bimgo-ai-web`'s `useDocumentContext.ts` (the fetch half) with
 * one change of address: 2.x asked one agent for one Revit's version, 2.5
 * names the year of the **selected instance**, because several Revits may be
 * running and each has its own context file. The item and response shapes are
 * the old client's `DocumentContextItem` / `*Response` types, which are the
 * shapes the agent still emits.
 */
import { agentClient, type AgentClient } from "./agentClient";
import { parseAskError } from "./askError";
import { bearer } from "./accessToken";

export interface ParameterInfo {
  id: string;
  name: string;
  definition?: "BuiltIn" | "Shared" | "Project";
  storageType?: "Double" | "Integer" | "String" | "ElementId" | "None";
}

export interface CadLinkMetadata {
  sourcePath?: string;
  isLinked?: boolean;
  importUnitLabel?: string;
  importScale?: number;
  transformOrigin?: { x: number; y: number; z: number };
  linkZRotationDeg?: number;
}

/** One entry of one type array in the context file. */
export interface DocumentContextItem {
  id: string;
  name: string;
  /** ElementType entries only. */
  familyName?: string;
  typeName?: string;
  typeParameters?: ParameterInfo[];
  instanceParameters?: ParameterInfo[];
  /** Family entries only. */
  familyParameters?: ParameterInfo[];
  /** Family entries: the exemplar FamilySymbol the parameters came from. */
  derivedFromTypeId?: string;
  derivedFromTypeName?: string;
  /** CADLink only. */
  cadLayers?: ParameterInfo[];
  cadBlocks?: ParameterInfo[];
  cadMetadata?: CadLinkMetadata;
  [key: string]: unknown;
}

/** `GET /document-context?revitVersion=` — the type keys and the file's meta. */
export interface DocumentContextKeys {
  keys: string[];
  /** Items per type key. */
  counts: Record<string, number>;
  documentName: string | null;
  documentId: string | null;
  documentPath: string | null;
  lastUpdated: string | null;
  language: string | null;
  displayNames: Record<string, string>;
  schemaVersion: number | null;
}

/** One hit of `&search=` — the item's identity plus the family fields. */
export interface DocumentContextSearchHit {
  type: string;
  id: string;
  name: string;
  familyName?: string;
  typeName?: string;
  derivedFromTypeId?: string;
  derivedFromTypeName?: string;
}

export class DocumentContextError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "DocumentContextError";
    this.status = status;
  }
}

/**
 * The plugin's index states (item BK), plus `unknown` when the agent has no
 * status file for the year — an old plugin, or no Revit running for it.
 * `missing` is "a document is open, nothing built yet": the build is the
 * user's click, never automatic, so Revit does no indexing work unasked.
 */
export type DocumentContextIndexState = "unknown" | "unavailable" | "missing" | "building" | "fresh" | "stale" | "failed";

export interface DocumentContextStatus {
  state: DocumentContextIndexState;
  generation: number | null;
  documentName: string | null;
  lastUpdated: string | null;
  error: string | null;
  /** The plugin can rebuild on request (it is new enough, and a consumer is active). */
  canRebuild: boolean;
}

const INDEX_STATES: readonly DocumentContextIndexState[] = ["unknown", "unavailable", "missing", "building", "fresh", "stale", "failed"];

export function parseStatusResponse(payload: unknown): DocumentContextStatus {
  const data = (payload && typeof payload === "object" ? payload : {}) as Record<string, unknown>;
  const raw = typeof data["state"] === "string" ? data["state"] : "unknown";
  return {
    state: (INDEX_STATES as readonly string[]).includes(raw) ? (raw as DocumentContextIndexState) : "unknown",
    generation: typeof data["generation"] === "number" ? data["generation"] : null,
    documentName: str(data["documentName"]),
    lastUpdated: str(data["lastUpdated"]),
    error: str(data["error"]),
    canRebuild: data["canRebuild"] === true,
  };
}

export interface DocumentContextClient {
  keys(revitVersion: string): Promise<DocumentContextKeys>;
  typeItems(revitVersion: string, type: string): Promise<DocumentContextItem[]>;
  search(revitVersion: string, query: string): Promise<DocumentContextSearchHit[]>;
  /** The index's status for the year — `unknown` when the agent has no status file. */
  status(revitVersion: string): Promise<DocumentContextStatus>;
  /** Ask the bound instance to rebuild its index. Resolves when the plugin *accepted*, not when it finished. */
  rebuild(targetSessionId: string): Promise<void>;
}

/**
 * The identity of the document a keys response describes — path and name, as
 * the old web's `getDocumentIdentity`. Two responses with different identities
 * come from different documents, and every per-type cache is stale.
 */
export function documentIdentity(keys: Pick<DocumentContextKeys, "documentPath" | "documentName">): string | null {
  if (keys.documentPath || keys.documentName) return `${keys.documentPath ?? ""}\u0000${keys.documentName ?? ""}`;
  return null;
}

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

export function parseKeysResponse(payload: unknown): DocumentContextKeys {
  const data = (payload && typeof payload === "object" ? payload : {}) as Record<string, unknown>;
  const keys = Array.isArray(data["keys"]) ? data["keys"].filter((k): k is string => typeof k === "string") : [];
  const names = data["displayNames"];
  const displayNames: Record<string, string> = {};
  if (names && typeof names === "object" && !Array.isArray(names)) {
    for (const [key, value] of Object.entries(names as Record<string, unknown>)) {
      if (typeof value === "string") displayNames[key] = value;
    }
  }
  const counts: Record<string, number> = {};
  const rawCounts = data["counts"];
  if (rawCounts && typeof rawCounts === "object" && !Array.isArray(rawCounts)) {
    for (const [key, value] of Object.entries(rawCounts as Record<string, unknown>)) {
      if (typeof value === "number") counts[key] = value;
    }
  }
  return {
    keys,
    counts,
    documentName: str(data["documentName"]),
    documentId: str(data["documentId"]),
    documentPath: str(data["documentPath"]),
    lastUpdated: str(data["lastUpdated"]),
    language: str(data["language"]),
    displayNames,
    schemaVersion: typeof data["schemaVersion"] === "number" ? data["schemaVersion"] : null,
  };
}

export function createDocumentContextClient(
  auth: { token: string; organizationId: string | null },
  client: AgentClient = agentClient,
): DocumentContextClient {
  async function request<T>(path: string, init: { method: "GET" | "POST"; body?: unknown }): Promise<T> {
    const response = await client.fetch(path, {
      method: init.method,
      headers: {
        Authorization: bearer(auth.token),
        ...(auth.organizationId ? { "X-Organization-Id": auth.organizationId } : {}),
        ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = parseAskError(response.status, payload, response.statusText);
      throw new DocumentContextError(response.status, parsed.message || `HTTP ${response.status}`);
    }
    return payload as T;
  }
  const get = <T,>(query: Record<string, string>) => request<T>(`/document-context?${new URLSearchParams(query).toString()}`, { method: "GET" });

  return {
    async keys(revitVersion) {
      return parseKeysResponse(await get<unknown>({ revitVersion }));
    },
    async typeItems(revitVersion, type) {
      const data = await get<{ items?: unknown }>({ revitVersion, type });
      return Array.isArray(data?.items) ? (data.items as DocumentContextItem[]) : [];
    },
    async search(revitVersion, query) {
      const data = await get<{ items?: unknown }>({ revitVersion, search: query });
      return Array.isArray(data?.items) ? (data.items as DocumentContextSearchHit[]) : [];
    },
    async status(revitVersion) {
      return parseStatusResponse(await request<unknown>(`/document-context/status?${new URLSearchParams({ revitVersion }).toString()}`, { method: "GET" }));
    },
    async rebuild(targetSessionId) {
      await request<unknown>("/document-context/rebuild", { method: "POST", body: { target_session_id: targetSessionId } });
    },
  };
}
